/**
 * Follow-Up Engine Mapper
 *
 * Pure conversion functions between UI types (FollowUpEngineContext)
 * and Supabase DB row shapes (follow_up_* tables).
 */

import type {
  DispositionRule,
  MessageTemplate,
  SequenceStep,
  FollowUpEngineConfig,
} from "@/data/FollowUpEngineContext";
import type { ExecutionMethod, FollowUpPriority } from "@/data/unifiedFollowUp";

// ── DB row shapes ─────────────────────────────────────────────────────────────

export interface FollowUpRuleRow {
  id: string;
  disposition_name: string;
  default_follow_up_days: number;
  execution_method: string;
  priority_level: string;
  active: boolean;
  escalation_days: number | null;
  manager_notify: boolean;
  auto_send_thank_you: boolean;
  created_at?: string;
}

export interface FollowUpTemplateRow {
  id: string;
  rule_id: string;
  type: string;
  label: string;
  body: string;
  auto_send: boolean;
  created_at?: string;
}

export interface FollowUpSequenceStepRow {
  id: string;
  rule_id: string;
  order_index: number;
  day_offset: number;
  action_type: string;
  template_id: string;
  auto_send: boolean;
  label: string;
  created_at?: string;
}

export interface FollowUpGlobalSettingsRow {
  id: string;
  thank_you_delay_minutes: number;
  escalation_enabled: boolean;
  ai_suggestions_enabled: boolean;
  updated_at?: string;
}

// ── Rule mappers ──────────────────────────────────────────────────────────────

export function toDbRule(rule: DispositionRule): FollowUpRuleRow {
  return {
    id: rule.id,
    disposition_name: rule.dispositionName,
    default_follow_up_days: rule.defaultFollowUpDays,
    execution_method: rule.executionMethod,
    priority_level: rule.priorityLevel,
    active: rule.active,
    escalation_days: rule.escalationDays ?? null,
    manager_notify: rule.managerNotify,
    auto_send_thank_you: rule.autoSendThankYou,
  };
}

export function toUiRule(row: FollowUpRuleRow): DispositionRule {
  return {
    id: row.id,
    dispositionName: row.disposition_name,
    defaultFollowUpDays: row.default_follow_up_days,
    executionMethod: row.execution_method as ExecutionMethod,
    priorityLevel: row.priority_level as FollowUpPriority,
    active: row.active,
    escalationDays: row.escalation_days ?? undefined,
    managerNotify: row.manager_notify,
    autoSendThankYou: row.auto_send_thank_you,
  };
}

// ── Template mappers ──────────────────────────────────────────────────────────

export function toDbTemplate(tpl: MessageTemplate): FollowUpTemplateRow {
  return {
    id: tpl.id,
    rule_id: tpl.dispositionRuleId,
    type: tpl.type,
    label: tpl.label,
    body: tpl.body,
    auto_send: tpl.autoSend,
  };
}

export function toUiTemplate(row: FollowUpTemplateRow): MessageTemplate {
  return {
    id: row.id,
    dispositionRuleId: row.rule_id,
    type: row.type as MessageTemplate["type"],
    label: row.label,
    body: row.body,
    autoSend: row.auto_send,
  };
}

// ── Sequence step mappers ─────────────────────────────────────────────────────

export function toDbSequenceStep(step: SequenceStep): FollowUpSequenceStepRow {
  return {
    id: step.id,
    rule_id: step.dispositionRuleId,
    order_index: step.order,
    day_offset: step.dayOffset,
    action_type: step.actionType,
    template_id: step.templateId,
    auto_send: step.autoSend,
    label: step.label,
  };
}

export function toUiSequenceStep(row: FollowUpSequenceStepRow): SequenceStep {
  return {
    id: row.id,
    dispositionRuleId: row.rule_id,
    order: row.order_index,
    dayOffset: row.day_offset,
    actionType: row.action_type as ExecutionMethod,
    templateId: row.template_id,
    autoSend: row.auto_send,
    label: row.label,
  };
}

// ── Global settings mappers ───────────────────────────────────────────────────

export function toDbGlobalSettings(
  settings: FollowUpEngineConfig["globalSettings"],
): Omit<FollowUpGlobalSettingsRow, "updated_at"> {
  return {
    id: "global",
    thank_you_delay_minutes: settings.thankYouDelayMinutes,
    escalation_enabled: settings.escalationEnabled,
    ai_suggestions_enabled: settings.aiSuggestionsEnabled,
  };
}

export function toUiGlobalSettings(
  row: FollowUpGlobalSettingsRow,
): FollowUpEngineConfig["globalSettings"] {
  return {
    thankYouDelayMinutes: row.thank_you_delay_minutes,
    escalationEnabled: row.escalation_enabled,
    aiSuggestionsEnabled: row.ai_suggestions_enabled,
  };
}
